/**
 * FieldGrid + Field — the Overview block of the Inspector grammar
 * (design-system §5.2).
 *
 * E0 (flush on the paper, no card): a two-column grid (`gap-x-8 gap-y-3`) with a
 * muted `w-40` label column and a `foreground` value column. Empty values render
 * a muted em-dash, never a blank cell. `JsonOrText` handles ambiguous columns
 * (renders parseable JSON via the `JsonViewer`, else monospace text).
 */
import * as React from "react";

import { cn } from "@/lib/utils";
import { JsonViewer } from "@/components/app/JsonViewer";

/** Render `null`/`undefined`/empty strings as a muted em-dash. */
export function FieldDash() {
  return <span className="text-muted-foreground">—</span>;
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

export interface FieldProps {
  /** The muted label (left column). */
  label: string;
  /**
   * The value (right column). When omitted/empty, a muted em-dash is shown so a
   * field is never a blank cell.
   */
  children?: React.ReactNode;
  /** Span both columns (long prose / JSON / wide values). */
  full?: boolean;
  className?: string;
}

/**
 * A single label/value row inside a `FieldGrid`. Label is `muted-foreground`,
 * value is `foreground`; both `text-sm`. Empty → em-dash.
 */
export function Field({ label, children, full, className }: FieldProps) {
  const empty = isEmpty(children) || (Array.isArray(children) && children.length === 0);
  return (
    <div
      className={cn(
        // Stack label-over-value when the container is narrow (e.g. the assistant
        // rail squeezes the page); switch to label|value columns once there is
        // room. Container-query based, so it reacts to the actual content width,
        // not the viewport.
        "grid grid-cols-1 gap-x-4 gap-y-0.5 @sm:grid-cols-[10rem_1fr] @sm:items-baseline",
        full && "@xl:col-span-2",
        className,
      )}
    >
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-foreground">
        {empty ? <FieldDash /> : children}
      </dd>
    </div>
  );
}

export interface FieldGridProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Two-column field grid (the Overview section body). Pure E0 — no border, no
 * card; structure comes from the heading above it (§2.1) + whitespace.
 */
export function FieldGrid({ children, className }: FieldGridProps) {
  return (
    <div className="@container">
      <dl
        className={cn(
          // Two field columns only when the container is genuinely wide (full
          // page); a single column when the assistant rail (or a small screen)
          // narrows the content area, so values never crush into a thin column.
          "grid grid-cols-1 gap-x-8 gap-y-3 @2xl:grid-cols-2",
          className,
        )}
      >
        {children}
      </dl>
    </div>
  );
}

export interface JsonOrTextProps {
  /** A value that may be a JSON string, an object, or plain text. */
  value: unknown;
  className?: string;
}

/**
 * Render a value as pretty JSON when it parses as JSON (string or object),
 * otherwise as wrapped monospace text. Empty → muted em-dash.
 */
export function JsonOrText({ value, className }: JsonOrTextProps) {
  if (isEmpty(value)) return <FieldDash />;

  if (typeof value === "object") {
    return <JsonViewer data={value} className={className} />;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        JSON.parse(trimmed);
        return <JsonViewer data={value} className={className} />;
      } catch {
        // fall through to text
      }
    }
    return (
      <pre
        className={cn(
          "whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs",
          className,
        )}
      >
        {value}
      </pre>
    );
  }

  return <span className={cn("text-sm", className)}>{String(value)}</span>;
}
